import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { assignmentsApi, type Assignment, type Sample } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { ActionButton, Alert, Panel, StatusBadge } from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { currentLocale } from '../../i18n/index.js';
import { testName, toneForAssignmentStatus } from '../work/status.js';
import { requestedTestName, requestedTestStandard } from './requested-tests.js';

/** Work nobody holds yet: shown as "Awaiting Assignment", not a status of its own (D235). */
export function isAwaitingAssignment(row: Pick<Assignment, 'status' | 'assignedTo'>): boolean {
  return row.status === 'not_started' && row.assignedTo === null;
}

/**
 * The sample's Lab Work.
 *
 * Accepting a sample turns each requested test into lab work awaiting
 * assignment (D229), so this section never asks which test: "Assign" opens
 * that work with the assign dialog open, and the person picks only who does
 * it (D235). A requested test that is not lab work says why — a free-text
 * work-order line, or a sample not accepted yet. "Assign a test" remains only
 * for a sample whose requested tests were never recorded (before 0017).
 * The backend re-checks every action (decision D73).
 */
export function SampleAssignmentsPanel({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();
  const mayView = can(PERMISSIONS.ASSIGNMENTS_VIEW);
  const mayAssign = can(PERMISSIONS.ASSIGNMENTS_REASSIGN) && sample.mayBeAssignedTests && !sample.isVoided;

  const work = useQuery({
    queryKey: ['assignments', { sampleId: sample.id }],
    queryFn: () => assignmentsApi.list({ sampleId: sample.id, pageSize: 100 }),
    enabled: mayView,
  });

  if (!mayView) return null;

  const requested = sample.requestedServices;
  const requestedById = new Map(requested.map((service) => [service.workOrderServiceId, service]));
  // In work-order line order, as the Requested Tests section lists them;
  // work without a requested test (before 0017) after it.
  const lineOf = (row: Assignment) =>
    (row.workOrderServiceId ? requestedById.get(row.workOrderServiceId)?.lineNumber : undefined) ??
    Number.MAX_SAFE_INTEGER;
  const rows = [...(work.data?.data ?? [])].sort((a, b) => lineOf(a) - lineOf(b));
  const covered = new Set(
    rows.filter((row) => row.status !== 'cancelled').map((row) => row.workOrderServiceId),
  );
  // Requested tests with no live lab work, and why (D231, D236).
  const uncovered = work.data
    ? requested.filter(
        (service) =>
          !covered.has(service.workOrderServiceId) &&
          service.state !== 'cancelled' &&
          (service.catalogueItemId === null || sample.mayBeAssignedTests),
      )
    : [];
  const awaitingAcceptance =
    requested.length > 0 && !sample.mayBeAssignedTests && !sample.isVoided && rows.length === 0;

  return (
    <Panel
      title={t('sampleWork.title')}
      description={t('sampleWork.hint')}
      actions={
        can(PERMISSIONS.ASSIGNMENTS_CREATE) &&
        requested.length === 0 &&
        sample.mayBeAssignedTests &&
        !sample.isVoided ? (
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() => navigate(`/work/assign?sampleId=${sample.id}`)}
          >
            {t('work.assignTest')}
          </ActionButton>
        ) : null
      }
    >
      {!sample.mayBeAssignedTests && !sample.isVoided ? (
        <Alert tone="warning" title={t('samples.mayNotBeTestedTitle')} description={t('samples.mayNotBeTestedBody')} />
      ) : null}
      {work.error ? (
        <ErrorNotice error={work.error} />
      ) : rows.length === 0 && uncovered.length === 0 ? (
        <p className="mt-stack text-body text-default-600" data-testid="lab-work-empty">
          {awaitingAcceptance ? t('sampleWork.createdOnAcceptance') : t('sampleWork.none')}
        </p>
      ) : (
        <ul className="mt-stack flex flex-col gap-stack" data-testid="lab-work-list">
          {rows.map((row) => {
            const service = row.workOrderServiceId ? requestedById.get(row.workOrderServiceId) : undefined;
            const standard = service
              ? requestedTestStandard(service.referenceStandard, service.standardVersion)
              : null;
            const awaiting = isAwaitingAssignment(row);
            return (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-stack border-s-2 border-default-200 ps-3"
                data-testid="lab-work-item"
              >
                <div className="min-w-0">
                  <p className="text-body font-medium text-default-900">
                    {testName(row.test, locale)}
                    {standard ? (
                      <span className="font-normal text-default-700">
                        {' — '}
                        <span dir="ltr">{standard}</span>
                      </span>
                    ) : null}
                  </p>
                  <p className="text-caption text-default-600">
                    {row.assignedTo?.displayName ?? t('work.unassigned')}
                  </p>
                </div>
                <StatusBadge
                  label={awaiting ? t('sampleWork.awaitingAssignment') : t(`status.test.${row.status}`)}
                  tone={awaiting ? 'warning' : toneForAssignmentStatus(row.status)}
                  size="sm"
                />
                {awaiting && mayAssign ? (
                  <ActionButton
                    priority="secondary"
                    size="sm"
                    className="ms-auto"
                    onPress={() => navigate(`/work/${row.id}?assign=1`)}
                  >
                    {t('work.assign')}
                  </ActionButton>
                ) : (
                  <ActionButton
                    priority="tertiary"
                    size="sm"
                    className="ms-auto"
                    onPress={() => navigate(`/work/${row.id}`)}
                  >
                    {t('common.open')}
                  </ActionButton>
                )}
              </li>
            );
          })}
          {uncovered.map((service) => (
            <li
              key={service.workOrderServiceId}
              className="flex flex-wrap items-center gap-stack border-s-2 border-dashed border-default-200 ps-3"
              data-testid="lab-work-uncovered"
            >
              <div className="min-w-0">
                <p className="text-body text-default-700">
                  {requestedTestName(service.descriptionAr, service.descriptionEn, locale) ??
                    t('common.notRecorded')}
                </p>
                <p className="text-caption text-default-600">
                  {service.catalogueItemId === null
                    ? t('sampleWork.notCatalogueTest')
                    : t('sampleWork.noLabWorkYet')}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
